import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Send } from 'lucide-react';
import { internalApi, fetchMe, type Me } from '../lib/hubApi';
import { ConfirmModal } from '../components/shared/ConfirmModal';

type HubAccessStatus = 'pending' | 'expired' | 'active' | 'inactive';

type HubAccessEntry = {
  email: string;
  sfOrgId: string;
  sfUserId: string | null;
  invitedBy: string;
  invitedAt: number;
  orgType: 'production' | 'sandbox';
  status: HubAccessStatus;
  firstSeenAt: number | null;
  lastSeenAt: number | null;
  deactivatedAt: number | null;
  deactivatedBy: string | null;
  expiresAt: number | null;
  baloClickedAt: number | null;
};

type StatusFilter = 'all' | HubAccessStatus;

const STATUSES: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Deactivated' },
  { value: 'expired', label: 'Expired' },
];

export function HubAccessPage() {
  const [entries, setEntries] = useState<HubAccessEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [search, setSearch] = useState('');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [me, setMe] = useState<Me | null>(null);
  const [pending, setPending] = useState<
    | { kind: 'deactivate' | 'reactivate'; entry: HubAccessEntry }
    | { kind: 'revoke'; entry: HubAccessEntry }
    | null
  >(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await internalApi.get<{ entries: HubAccessEntry[] }>('/hub-access');
      setEntries(data.entries);
    } catch (e) {
      const m = (e as { response?: { data?: { error?: string } } }).response?.data?.error;
      setError(m ?? (e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void fetchMe().then(setMe);
  }, []);

  const counts = useMemo(() => {
    const c = { total: entries.length, active: 0, pending: 0, inactive: 0, expired: 0 };
    for (const e of entries) c[e.status]++;
    return c;
  }, [entries]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return entries
      .filter((e) => statusFilter === 'all' || e.status === statusFilter)
      .filter(
        (e) =>
          q === '' ||
          e.email.toLowerCase().includes(q) ||
          e.sfOrgId.toLowerCase().includes(q) ||
          e.invitedBy.toLowerCase().includes(q),
      );
  }, [entries, statusFilter, search]);

  const confirmAction = async () => {
    if (!pending) return;
    try {
      if (pending.kind === 'revoke') {
        const { data } = await internalApi.post<{ ok: true; revoked: number }>(
          '/hub-access/revoke-invite',
          { email: pending.entry.email, sfOrgId: pending.entry.sfOrgId },
        );
        toast.success(`Revoked ${data.revoked} pending invite${data.revoked === 1 ? '' : 's'}`);
      } else {
        if (!pending.entry.sfUserId) {
          toast.error('Cannot toggle: hub user has not activated yet');
          return;
        }
        await internalApi.post('/hub-access/set-active', {
          sfOrgId: pending.entry.sfOrgId,
          sfUserId: pending.entry.sfUserId,
          active: pending.kind === 'reactivate',
        });
        toast.success(pending.kind === 'reactivate' ? 'Reactivated' : 'Deactivated');
      }
      setPending(null);
      await load();
    } catch (e) {
      const m = (e as { response?: { data?: { error?: string } } }).response?.data?.error;
      toast.error(m ?? (e as Error).message);
      setPending(null);
    }
  };

  return (
    <section className="space-y-6 max-w-5xl">
      <header className="flex items-start gap-4">
        <div className="flex-1 min-w-0 space-y-2">
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-neutral-500">
            Hub access management
          </div>
          <h1 className="text-[26px] leading-tight font-semibold text-crux-light">
            Invited client admins
          </h1>
          <p className="text-sm text-neutral-400">
            Track who&apos;s been invited to OrgIQ Hub, whether they&apos;ve activated, and revoke
            access if needed.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setInviteOpen(true)}
          disabled={!me?.sfOrgId}
          className="shrink-0 flex items-center gap-2 px-3.5 py-2 rounded-md bg-sfblue hover:bg-sfblue/85 disabled:opacity-50 text-sm font-medium text-white transition-colors"
          title={me?.sfOrgId ? undefined : 'Connect to an org first'}
        >
          <Send size={14} />
          Invite client admin
        </button>
      </header>

      {error && (
        <div className="px-4 py-3 rounded-lg border border-danger/40 bg-danger/10 text-sm text-danger">
          {error}
        </div>
      )}

      <div className="grid grid-cols-4 gap-3">
        <StatTile label="Total" value={counts.total} />
        <StatTile label="Active" value={counts.active} tone="success" />
        <StatTile label="Pending" value={counts.pending} tone="warning" />
        <StatTile label="Deactivated" value={counts.inactive} tone="muted" />
      </div>

      <div className="flex flex-wrap gap-2 items-center text-xs">
        <div className="flex items-center gap-1.5">
          <span className="text-neutral-500">Status</span>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            className="px-2.5 py-1.5 rounded-md border border-border bg-surface-2 text-crux-light focus:outline-none focus:border-border-strong"
          >
            {STATUSES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search email, org, inviter…"
          aria-label="Search hub access entries"
          className="ml-auto px-3 py-1.5 rounded-md border border-border bg-surface-2 text-xs w-72 text-crux-light placeholder:text-neutral-500 focus:outline-none focus:border-border-strong"
        />
      </div>

      {loading && entries.length === 0 ? (
        <p className="text-neutral-500 text-sm">Loading…</p>
      ) : filtered.length === 0 ? (
        <p className="text-neutral-500 text-sm py-4">No entries match.</p>
      ) : (
        <div className="rounded-lg border border-border-subtle bg-surface-2 shadow-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-surface-1/60">
              <tr className="text-[10px] font-semibold uppercase tracking-[0.14em] text-neutral-500">
                <th className="text-left py-2.5 px-4">Email</th>
                <th className="text-left py-2.5 px-4">Org</th>
                <th className="text-left py-2.5 px-4">Status</th>
                <th className="text-left py-2.5 px-4">Invited</th>
                <th className="text-left py-2.5 px-4">Last seen</th>
                <th className="text-right py-2.5 px-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((e) => (
                <Row
                  key={`${e.sfOrgId}::${e.email}::${e.sfUserId ?? 'pending'}`}
                  entry={e}
                  onDeactivate={() => setPending({ kind: 'deactivate', entry: e })}
                  onReactivate={() => setPending({ kind: 'reactivate', entry: e })}
                  onRevoke={() => setPending({ kind: 'revoke', entry: e })}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <InviteModal
        open={inviteOpen}
        sfOrgId={me?.sfOrgId ?? null}
        onClose={() => setInviteOpen(false)}
        onInvited={async () => {
          setInviteOpen(false);
          await load();
        }}
      />

      <ConfirmModal
        open={pending !== null}
        title={
          pending?.kind === 'revoke'
            ? `Revoke invite for ${pending.entry.email}?`
            : pending?.kind === 'deactivate'
              ? `Deactivate ${pending.entry.email}?`
              : pending
                ? `Reactivate ${pending.entry.email}?`
                : ''
        }
        body={
          pending?.kind === 'revoke'
            ? 'All pending invites for this email + org will be removed. They will not be able to activate from any existing email link.'
            : pending?.kind === 'deactivate'
              ? 'They will be locked out of /hub immediately. Existing sessions on /hub will hit the next 401 and be bounced to /hub-login.'
              : 'They will regain access to /hub from /hub-login (no new invite needed).'
        }
        reversibilityNote={
          pending?.kind === 'deactivate'
            ? 'Reversible. Reactivate restores access.'
            : pending?.kind === 'reactivate'
              ? 'Reversible. Deactivate revokes access again.'
              : 'Irreversible for this token. You can always issue a fresh invite.'
        }
        confirmLabel={
          pending?.kind === 'revoke'
            ? 'Revoke invite'
            : pending?.kind === 'deactivate'
              ? 'Deactivate'
              : 'Reactivate'
        }
        onCancel={() => setPending(null)}
        onConfirm={confirmAction}
      />
    </section>
  );
}

function StatTile({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: 'success' | 'warning' | 'muted';
}) {
  const valueColor =
    tone === 'success'
      ? 'text-success'
      : tone === 'warning'
        ? 'text-warning'
        : tone === 'muted'
          ? 'text-neutral-500'
          : 'text-crux-light';
  return (
    <div className="rounded-lg border border-border-subtle bg-surface-2 shadow-card px-4 py-3">
      <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-neutral-500">
        {label}
      </div>
      <div className={`text-2xl font-semibold tabular-nums leading-none mt-1.5 ${valueColor}`}>
        {value}
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: HubAccessStatus }) {
  const styles: Record<HubAccessStatus, string> = {
    active: 'bg-success/15 border-success/40 text-success',
    pending: 'bg-warning/15 border-warning/40 text-warning',
    inactive: 'bg-neutral-700/30 border-neutral-600 text-neutral-400',
    expired: 'bg-danger/10 border-danger/30 text-danger',
  };
  const label: Record<HubAccessStatus, string> = {
    active: 'Active',
    pending: 'Pending',
    inactive: 'Deactivated',
    expired: 'Expired',
  };
  return (
    <span
      className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-md border ${styles[status]}`}
    >
      {label[status]}
    </span>
  );
}

function Row({
  entry,
  onDeactivate,
  onReactivate,
  onRevoke,
}: {
  entry: HubAccessEntry;
  onDeactivate: () => void;
  onReactivate: () => void;
  onRevoke: () => void;
}) {
  const invited = new Date(entry.invitedAt).toLocaleDateString();
  const lastSeen = entry.lastSeenAt
    ? new Date(entry.lastSeenAt).toLocaleDateString()
    : entry.status === 'active' || entry.status === 'inactive'
      ? '-'
      : 'never';
  return (
    <tr className="border-t border-border-subtle">
      <td className="py-2.5 px-4">
        <div className="text-crux-light">{entry.email}</div>
        <div className="text-[11px] text-neutral-500 mt-0.5">invited by {entry.invitedBy}</div>
        {entry.baloClickedAt ? (
          <div className="text-[11px] text-grow mt-0.5">
            Booking link clicked {new Date(entry.baloClickedAt).toLocaleDateString()}
          </div>
        ) : null}
      </td>
      <td className="py-2.5 px-4 font-mono text-xs text-neutral-400">
        {entry.sfOrgId.slice(0, 15)}
        <span className="ml-2 text-[10px] uppercase tracking-wider text-neutral-500">
          {entry.orgType}
        </span>
      </td>
      <td className="py-2.5 px-4">
        <StatusBadge status={entry.status} />
      </td>
      <td className="py-2.5 px-4 text-xs text-neutral-400">{invited}</td>
      <td className="py-2.5 px-4 text-xs text-neutral-400">{lastSeen}</td>
      <td className="py-2.5 px-4 text-right">
        <RowActions
          entry={entry}
          onDeactivate={onDeactivate}
          onReactivate={onReactivate}
          onRevoke={onRevoke}
        />
      </td>
    </tr>
  );
}

function InviteModal({
  open,
  sfOrgId,
  onClose,
  onInvited,
}: {
  open: boolean;
  sfOrgId: string | null;
  onClose: () => void;
  onInvited: () => void | Promise<void>;
}) {
  const [email, setEmail] = useState('');
  const [sfUserId, setSfUserId] = useState('');
  const [busy, setBusy] = useState(false);

  // Reset fields whenever the modal opens so a previous submission doesn't
  // leak in. Cheaper than a key-based remount.
  useEffect(() => {
    if (open) {
      setEmail('');
      setSfUserId('');
    }
  }, [open]);

  if (!open) return null;

  const submit = async () => {
    if (busy) return;
    if (!email.trim()) {
      toast.error('Email required');
      return;
    }
    if (!sfOrgId) {
      toast.error('Org context not loaded. Try again in a moment');
      return;
    }
    setBusy(true);
    try {
      await internalApi.post('/invite', {
        email: email.trim(),
        sfOrgId,
        sfUserId: sfUserId.trim() || undefined,
      });
      toast.success(`Invitation sent to ${email.trim()}`);
      await onInvited();
    } catch (err) {
      const m = (err as { response?: { data?: { error?: string } } }).response?.data?.error;
      toast.error(m ?? (err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const inputClass =
    'mt-1.5 w-full px-3 py-2 rounded-md border border-border bg-surface-1 text-sm text-crux-light placeholder:text-neutral-500 focus:outline-none focus:border-border-strong';
  const labelClass = 'text-[10px] font-semibold uppercase tracking-[0.14em] text-neutral-500';

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 px-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-lg border border-border-subtle bg-surface-2 shadow-card p-5 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-neutral-500">
            New invitation
          </div>
          <h3 className="text-lg font-semibold text-crux-light mt-1">Invite a client admin</h3>
          <p className="text-xs text-neutral-400 mt-1.5">
            Sends a 14-day single-use activation link. The admin signs in with their Salesforce
            user, and no separate OrgIQ password is created.
          </p>
        </div>

        <label className="block">
          <span className={labelClass}>Email</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="admin@client.com"
            autoFocus
            className={inputClass}
          />
        </label>

        <label className="block">
          <span className={labelClass}>
            Salesforce user ID <span className="opacity-70 lowercase">(optional)</span>
          </span>
          <input
            value={sfUserId}
            onChange={(e) => setSfUserId(e.target.value)}
            placeholder="005…"
            className={`${inputClass} font-mono`}
          />
        </label>

        <p className="text-[11px] text-neutral-500">
          Org: <span className="font-mono">{sfOrgId?.slice(0, 15) ?? '-'}</span>
        </p>

        <div className="flex justify-end gap-2 pt-3 border-t border-border-subtle">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-md border border-border bg-surface-1 text-crux-light text-sm hover:bg-surface-hover hover:border-border-strong transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            className="px-3.5 py-1.5 rounded-md bg-sfblue hover:bg-sfblue/85 text-sm font-medium text-white disabled:opacity-50 transition-colors"
          >
            {busy ? 'Sending…' : 'Send invitation'}
          </button>
        </div>
      </div>
    </div>
  );
}

function RowActions({
  entry,
  onDeactivate,
  onReactivate,
  onRevoke,
}: {
  entry: HubAccessEntry;
  onDeactivate: () => void;
  onReactivate: () => void;
  onRevoke: () => void;
}) {
  const btn = 'px-2.5 py-1 rounded-md border text-xs transition-colors';
  if (entry.status === 'pending') {
    return (
      <button
        type="button"
        onClick={onRevoke}
        className={`${btn} border-border bg-surface-1 text-neutral-400 hover:text-crux-light hover:bg-surface-hover hover:border-border-strong`}
      >
        Revoke invite
      </button>
    );
  }
  if (entry.status === 'expired') {
    return (
      <span className="text-[11px] text-neutral-500">Re-issue from Internal → Users invite</span>
    );
  }
  if (entry.status === 'active') {
    return (
      <button
        type="button"
        onClick={onDeactivate}
        className={`${btn} border-danger/40 bg-danger/10 text-danger hover:bg-danger/15 hover:border-danger/60`}
      >
        Deactivate
      </button>
    );
  }
  // inactive
  return (
    <button
      type="button"
      onClick={onReactivate}
      className={`${btn} border-success/40 bg-success/10 text-success hover:bg-success/15 hover:border-success/60`}
    >
      Reactivate
    </button>
  );
}
